import React from 'react';
import classnames from 'classnames';
import styles from './CreateContactModal.module.scss';
import { useForm, useWatch } from 'react-hook-form';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { FormControl } from 'react-bootstrap';
import restFactory from 'v2/utils/restFactory';
import { Contact, DataVendor } from 'v2/models';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import useGetContacts from 'v2/hooks/useGetContacts';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import ContactFormForm, { ContactFormFields } from './ContactForm';
import ExistingContactBox from './ExistingContactBox';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { PostProcessKeys, TranslationKeys } from 'translations';

interface CreateContactModalProps {
  show: boolean;
  dataVendor?: DataVendor;
  onClose?: () => void;
}

const CreateContactModal = ({
  show,
  dataVendor,
  onClose,
}: CreateContactModalProps) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    reset: resetForm,
    formState: { isValid },
  } = useForm<ContactFormFields>({ mode: 'onChange' });
  const emailValue = useWatch({ control, name: 'email' });

  const { data: contactData, isSuccess: isEmailChangeSuccess } = useGetContacts(
    { email: emailValue }
  );

  const history = useHistory();

  const { isLoading: isContactMutateLoading, mutate: contactMutate } =
    useMutation(
      async (data: ContactFormFields) => {
        return await restFactory.post<Contact>(`/dj/api/v1/contact/`, data);
      },
      {
        onSuccess: (contact) => {
          toast.success(`New ${t(TranslationKeys.contact_one)} saved.`);

          const contactDetailRedirection = `/contact-details/${contact.id}/detail`;

          if (dataVendor) {
            history.push(contactDetailRedirection, {
              counterToGoBack: -1,
              backLinkTitle: dataVendor.name,
            });
          } else {
            history.push(contactDetailRedirection);
          }
        },
        onError: () => {
          toast.error(`Error saving ${t(TranslationKeys.contact_one)}.`);
        },
      }
    );

  const onSubmit = async (data: ContactFormFields) => {
    if (isValid) {
      contactMutate(data);
    }
  };

  const closeModal = () => {
    resetForm({
      email: '',
      first_name: '',
      last_name: '',
      title: '',
      company_name: '',
      data_vendor: undefined,
    });
    onClose?.();
  };

  return (
    <ConfirmationModal
      className={styles.viewModals}
      centered={true}
      animation={true}
      show={show}
      onCancel={closeModal}
      okBtnText="Save"
      onOk={handleSubmit(onSubmit)}
      okBtnDisabled={!isValid}
      isLoading={isContactMutateLoading}
      title={`Add ${t(TranslationKeys.contact_one, {
        postProcess: PostProcessKeys.TitleCase,
      })}`}
      onEntered={() => {
        control._fields.email?._f.ref.focus?.();
      }}
    >
      <>
        <p className={classnames(styles.modalInstructions)}>
          Start by entering the {t(TranslationKeys.contact_one)}’s email
          address.
        </p>

        <FormLabel label="Email" required={false} />
        <div
          className={classnames('d-flex algin-items-center flex-column', {
            [styles.emailError]: isEmailChangeSuccess && contactData?.count,
          })}
        >
          <FormControl
            type="text"
            placeholder="Email Address"
            aria-label="email-input"
            autoComplete="off"
            maxLength={255}
            data-cy="contact:create-email-field"
            {...register('email', {
              required: true,
              validate: (value) => yup.string().email().isValidSync(value),
            })}
          />
          <div className="d-none invalid-email">
            This email address already belongs to a{' '}
            {t(TranslationKeys.contact_one)} in your database.
          </div>
        </div>

        {isEmailChangeSuccess && contactData?.count ? (
          <ExistingContactBox
            contact={contactData?.results[0]}
            email={emailValue}
            dataVendor={dataVendor}
          />
        ) : (
          <div className="mt-5">
            <ContactFormForm
              control={control}
              readOnly={!isEmailChangeSuccess}
              dataVendor={dataVendor}
            />
          </div>
        )}
      </>
    </ConfirmationModal>
  );
};

export default CreateContactModal;
